/**
 * Extraction orchestration — build doc §4.2 Flow A, rules mode.
 *
 * Runs in the browser under the signed-in user's JWT, so every write is
 * RLS-enforced and audit-attributed to the human who triggered it. The Claude
 * path (Sonnet via the extract-document edge function) replaces the
 * `extractProvisions` call only; everything downstream is identical.
 */

import {
  deriveObligations,
  extractProvisions,
  verifyVerbatim,
  RULES_ENGINE_VERSION,
  type ExtractionResult,
  type StructuredValue,
} from "@argus/engine";
import { supabase } from "@/lib/supabase/client";
import { getDocument, updateDocument, type DocumentRow } from "@/lib/api/documents";
import { insertProvisions, deleteProvisionsForDocument } from "@/lib/api/provisions";
import { upsertMfnClauses, type UpsertMfnClauseInput } from "@/lib/api/mfn";
import { insertObligations, type InsertObligationInput } from "@/lib/api/obligations";
import { enforceMfnForFund } from "@/lib/pipeline/continuous-mfn";

export interface ExtractionRunSummary {
  provisionCount: number;
  mfnClauseCount: number;
  obligationCount: number;
  engine: string;
  warnings: string[];
}

interface AiProvision {
  category: string;
  rawText: string;
  summary: string;
  structuredValue: StructuredValue | null;
  pageNumber: number;
  confidence: number;
}

interface AiExtractionResponse {
  engine?: string;
  warnings?: string[];
  provisions?: AiProvision[];
}

/**
 * Try the Claude path (extract-document edge function). Returns null when the
 * function isn't deployed or the ANTHROPIC_API_KEY secret isn't set — the
 * caller falls back to the deterministic rules engine. The UI never talks to
 * Anthropic directly (anti-pattern §17.6).
 */
async function tryAiExtraction(documentId: string): Promise<ExtractionResult | null> {
  try {
    const response = await supabase.functions.invoke<AiExtractionResponse>("extract-document", {
      body: { document_id: documentId },
    });
    const data = response.data;
    if (response.error || !data || !Array.isArray(data.provisions)) return null;
    return {
      engine: String(data.engine ?? "claude"),
      warnings: Array.isArray(data.warnings) ? data.warnings.map(String) : [],
      provisions: data.provisions.map((p) => ({
        category: p.category as ExtractionResult["provisions"][number]["category"],
        rawText: p.rawText,
        summary: p.summary,
        structuredValue: p.structuredValue,
        pageNumber: p.pageNumber,
        confidence: p.confidence,
      })),
    };
  } catch {
    return null;
  }
}

export async function runExtraction(documentId: string): Promise<ExtractionRunSummary> {
  const doc: DocumentRow = await getDocument(documentId);
  if (!doc.extracted_text || doc.extracted_text.trim().length === 0) {
    await updateDocument(doc.id, {
      status: "failed",
      error_message:
        "No machine-readable text. The file may be a scanned image — OCR lands with the Textract integration.",
    });
    throw new Error("Document has no extracted text to process.");
  }

  await updateDocument(doc.id, { status: "extracting", error_message: null });

  try {
    // Re-runs replace prior output (provisions cascade to mfn_clauses + obligations).
    await deleteProvisionsForDocument(doc.id);

    // Claude path when deployed + keyed; deterministic rules engine otherwise.
    const result = (await tryAiExtraction(doc.id)) ?? extractProvisions(doc.extracted_text);

    // Hallucination guard (build doc §10.7): drop non-verbatim excerpts and say so.
    const bad = new Set(verifyVerbatim(doc.extracted_text, result.provisions));
    if (bad.size > 0) {
      result.warnings.push(
        `${bad.size} extracted provision(s) failed the verbatim guard and were discarded.`,
      );
      result.provisions = result.provisions.filter((_, i) => !bad.has(i));
    }

    const inserted = await insertProvisions(
      result.provisions.map((p) => ({
        tenant_id: doc.tenant_id,
        document_id: doc.id,
        fund_id: doc.fund_id,
        lp_id: doc.lp_id,
        category: p.category,
        raw_text: p.rawText,
        summary: p.summary,
        structured_value: p.structuredValue,
        page_number: p.pageNumber,
        confidence: p.confidence,
        metadata: { engine: result.engine },
      })),
    );

    // Materialize MFN clauses for the engine (build doc §9.1).
    const mfnClauses: UpsertMfnClauseInput[] = [];
    inserted.forEach((row) => {
      if ((row.category === "mfn" || row.category === "most_favored_nation_express") && row.lp_id) {
        const sv = row.structured_value;
        mfnClauses.push({
          tenant_id: doc.tenant_id,
          provision_id: row.id,
          fund_id: doc.fund_id,
          lp_id: row.lp_id,
          scope: sv?.mfn_scope ?? "full",
          scope_details: sv?.mfn_size_based ? { size_based: true } : null,
          threshold_commitment_usd: sv?.threshold_usd ?? null,
          excluded_categories: sv?.mfn_excluded_categories ?? [],
          fund_scope:
            row.category === "most_favored_nation_express"
              ? "this_fund_only"
              : (sv?.mfn_fund_scope ?? "this_fund_only"),
        });
      }
    });
    await upsertMfnClauses(mfnClauses);

    // Continuous MFN enforcement (build doc — 2026-07-01 decision): an
    // extraction that materializes/updates MFN clauses changes the facts the
    // engine reasons over — re-evaluate the fund. Fire-and-forget: never
    // block or fail the extraction on enforcement. 2026-07-03 audit fix:
    // enforceMfnForFund can now throw on a genuine core-step failure — log it
    // instead of swallowing to `undefined` a second time, so a real failure
    // still leaves a trace even though the extraction itself must not be
    // blocked by it. sourceRef points at the document since a single
    // extraction can upsert several clauses at once — there is no single
    // clause id to attribute the run to.
    if (mfnClauses.length > 0) {
      void enforceMfnForFund({
        tenantId: doc.tenant_id,
        fundId: doc.fund_id,
        source: "clause_changed",
        sourceRef: doc.id,
      }).catch((err: unknown) => {
        console.error(
          `[run-extraction] continuous MFN enforcement failed for fund=${doc.fund_id}:`,
          err,
        );
      });
    }

    // Obligation calendar entries (build doc §4.2 Flow C).
    const reference = doc.executed_date ? new Date(`${doc.executed_date}T00:00:00Z`) : new Date();
    const drafts = deriveObligations(
      inserted.map((row) => ({
        category: row.category,
        rawText: row.raw_text,
        summary: row.summary ?? "",
        structuredValue: row.structured_value,
        pageNumber: row.page_number ?? 1,
        confidence: Number(row.confidence ?? 0),
      })),
      reference,
    );
    const docLpId = doc.lp_id;
    const obligationRows: InsertObligationInput[] =
      docLpId === null
        ? []
        : drafts.flatMap((d) => {
            const prov = inserted[d.provisionIndex];
            if (!prov) return [];
            return [
              {
                tenant_id: doc.tenant_id,
                provision_id: prov.id,
                fund_id: doc.fund_id,
                lp_id: docLpId,
                title: d.title,
                description: d.description,
                obligation_type: d.obligationType,
                frequency: d.frequency,
                next_due_date: d.nextDueDate,
              },
            ];
          });
    await insertObligations(obligationRows);

    await updateDocument(doc.id, { status: "extracted" });

    return {
      provisionCount: inserted.length,
      mfnClauseCount: mfnClauses.length,
      obligationCount: obligationRows.length,
      engine: result.engine,
      warnings: result.warnings,
    };
  } catch (err) {
    await updateDocument(doc.id, {
      status: "failed",
      error_message: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}

export const EXTRACTION_ENGINE_LABEL = RULES_ENGINE_VERSION;
